'use client';

import {
  InternalNetworkCredentialsManagerContent,
  SettingsSection,
  toInternalNetworkCredentialOwnedUpdateBody,
  type InternalNetworkCredentialsManagerCallbacks,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  useCreateInternalNetworkCredential,
  useDeleteInternalNetworkCredential,
  useInternalNetworkCredentialVerificationReadiness,
  useUpdateInternalNetworkCredential,
  useVerifyInternalNetworkCredential,
} from '../../../../hooks/api/useInternalNetworkCredentials';
import {
  useInternalNetworkCredentials,
  useInternalNetworkDetail,
} from '../../../../hooks/api/useSurfaceSettings';
import { useInternalNetworkSSE } from '../../../../hooks/useInternalNetworkSSE';

type InternalCredentialsSettingsPanelProps = {
  networkId: string;
  isReadOnly: boolean;
};

export function InternalCredentialsSettingsPanel({
  networkId,
  isReadOnly,
}: InternalCredentialsSettingsPanelProps) {
  const t = useTranslations('internalNetwork.settings.credentials');

  useInternalNetworkSSE({ networkId });
  const { data: network } = useInternalNetworkDetail(networkId);
  const credentialsQuery = useInternalNetworkCredentials(networkId);
  const { canVerify, blockReason } =
    useInternalNetworkCredentialVerificationReadiness({
      networkId,
      pollWhileWaiting: true,
    });
  const createCredential = useCreateInternalNetworkCredential(networkId);
  const updateCredential = useUpdateInternalNetworkCredential(networkId);
  const deleteCredential = useDeleteInternalNetworkCredential(networkId);
  const verifyCredential = useVerifyInternalNetworkCredential(networkId);

  const callbacks: InternalNetworkCredentialsManagerCallbacks = {
    onCreate: (body) => createCredential.mutateAsync(body),
    onUpdate: ({ credentialId, body }) =>
      updateCredential.mutateAsync({ credentialId, body }),
    onDelete: async (credentialId) => {
      await deleteCredential.mutateAsync(credentialId);
    },
    onVerify: (credentialId) => verifyCredential.mutateAsync(credentialId),
    onSetOwned: async ({ credential, isOwned }) => {
      await updateCredential.mutateAsync({
        credentialId: credential.id,
        body: toInternalNetworkCredentialOwnedUpdateBody({
          credential,
          isOwned,
        }),
      });
    },
  };

  return (
    <SettingsSection title={t('title')} description={t('sectionDescription')}>
      <VStack alignItems="stretch" gap={4}>
        <InternalNetworkCredentialsManagerContent
          credentials={credentialsQuery.data?.data ?? []}
          isLoading={credentialsQuery.isLoading}
          isSaving={createCredential.isPending || updateCredential.isPending}
          canVerify={canVerify}
          verificationBlockReason={blockReason}
          isReadOnly={isReadOnly}
          defaultDomain={network?.domainNames?.[0]}
          {...callbacks}
        />
      </VStack>
    </SettingsSection>
  );
}
